// Author names and message text come from the customer's Slack workspace and
// are untrusted: mrkdwn is parsed into elements, never injected as HTML. `ts`
// fields are Slack message timestamps ("1718012345.000200" — epoch seconds
// with a uniqueness suffix).

import { isString, isStringRecord } from "../utils/schemaUtils";

export interface SlackMessageItem {
  ts: string;
  // The author's display name, already resolved from the user ID.
  author: string;
  // Slack mrkdwn source.
  text: string;
  isBot?: boolean;
  permalink?: string;
}

export interface SlackMessagesPayload {
  type: "slack_messages";
  channelId: string;
  // Channel name without the leading '#'.
  channelName: string;
  // Present when the payload is a single thread.
  threadTs?: string;
  messages: SlackMessageItem[];
  // Bare Slack user id → display name for `<@UID>` tokens in message text.
  mentions?: Record<string, string>;
}

const isSlackMessageItem = (v: unknown): v is SlackMessageItem => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    isString(r.ts) &&
    isString(r.author) &&
    isString(r.text) &&
    (r.isBot === undefined || typeof r.isBot === "boolean") &&
    (r.permalink === undefined || isString(r.permalink))
  );
};

export const parseSlackMessagesPayload = (v: unknown): SlackMessagesPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "slack_messages") return null;
  if (!isString(r.channelId)) return null;
  if (!isString(r.channelName)) return null;
  if (r.threadTs !== undefined && !isString(r.threadTs)) return null;
  if (!Array.isArray(r.messages) || !r.messages.every(isSlackMessageItem)) return null;
  if (r.mentions !== undefined && !isStringRecord(r.mentions)) return null;
  return {
    type: "slack_messages",
    channelId: r.channelId,
    channelName: r.channelName,
    threadTs: r.threadTs,
    messages: r.messages,
    mentions: r.mentions,
  };
};
